import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, Building2, Briefcase, Clock, Zap, Calendar } from 'lucide-react';
import api from '../../../services/api';
import { AuthContext } from '../../../context/AuthContext';

const Attendance = () => {
  const { user } = useContext(AuthContext);
  const [history, setHistory] = useState([]);
  const [overtimeSummary, setOvertimeSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Set default attendance type based on user work location
  const [attendanceType, setAttendanceType] = useState(user?.workLocation || 'OFFICE');
  const [meetingReason, setMeetingReason] = useState('');
  const [workReport, setWorkReport] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [hasApprovedWfhToday, setHasApprovedWfhToday] = useState(false);
  const [globalSettings, setGlobalSettings] = useState(null);

  // Hybrid schedule state
  const [todayHybridSchedule, setTodayHybridSchedule] = useState(null);

  const [graceReason, setGraceReason] = useState('');
  const [graceLoading, setGraceLoading] = useState(false);

  const [showCameraModal, setShowCameraModal] = useState(false);
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraAction, setCameraAction] = useState('CLOCK_IN');

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const [res, otRes] = await Promise.all([
        api.get('/attendance/me'),
        api.get('/attendance/overtime-summary').catch(() => ({ data: null }))
      ]);
      setHistory(res.data);
      if (otRes?.data) {
        setOvertimeSummary(otRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch attendance history', err);
    } finally {
      setLoading(false);
    }
  };

  const checkWfhStatus = async () => {
    try {
      const res = await api.get('/attendance/wfh/me');
      const today = new Date();
      today.setHours(0,0,0,0);
      const approvedWfh = res.data.find(req => {
        const start = new Date(req.startDate);
        start.setHours(0,0,0,0);
        const end = new Date(req.endDate);
        end.setHours(0,0,0,0);
        return req.status === 'APPROVED' && today >= start && today <= end;
      });
      const hasApproved = !!approvedWfh;
      if (hasApproved) {
        setHasApprovedWfhToday(true);
        if (user?.workLocation === 'OFFICE') {
          setAttendanceType('WFH'); // Default to WFH if office employee has an approved request
        }
      }
      return hasApproved;
    } catch (err) {
      console.error('Failed to fetch WFH requests', err);
      return false;
    }
  };

  const fetchHybridSchedule = async (hasWfhPass = false) => {
    if (user?.workLocation !== 'HYBRID') return;
    try {
      const res = await api.get('/employees/me/hybrid-schedule');
      const sched = res.data || {};

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayDateStr = `${year}-${month}-${day}`;

      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const todayDayName = dayNames[now.getDay()];

      // 1. Check specific date override first
      const overrides = sched.dateOverrides || [];
      const override = overrides.find(o => o.date === todayDateStr);

      if (override) {
        setTodayHybridSchedule({
          type: override.type,
          isOverride: true,
          reason: override.reason,
          dateStr: todayDateStr
        });
        if (override.type === 'WFH') {
          setAttendanceType('WFH');
        } else if (!hasWfhPass) {
          setAttendanceType('OFFICE');
        }
        return;
      }

      // 2. Check weekly recurring schedule
      const weekly = sched.weeklyDays || {};
      const assigned = weekly[todayDayName] || 'OFFICE';
      setTodayHybridSchedule({
        type: assigned,
        isOverride: false,
        dayName: todayDayName,
        dateStr: todayDateStr
      });

      if (assigned === 'WFH') {
        setAttendanceType('WFH');
      } else if (!hasWfhPass) {
        setAttendanceType('OFFICE');
      }
    } catch (err) {
      console.error('Failed to load hybrid schedule', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setGlobalSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch settings', err);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchSettings();
    const init = async () => {
      const hasWfhPass = await checkWfhStatus();
      if (user?.workLocation === 'HYBRID') {
        await fetchHybridSchedule(hasWfhPass);
      }
    };
    init();
  }, [user]);


  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        return reject(new Error('Geolocation is not supported by your browser.'));
      }
      // Try high accuracy first (15s timeout, allow up to 30s cached position if available)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            return reject(new Error('Location permission is blocked. Please allow location access in your browser settings.'));
          }
          // Fallback to standard accuracy if indoor high-accuracy GPS timed out
          navigator.geolocation.getCurrentPosition(
            (fallbackPos) => {
              resolve({
                latitude: fallbackPos.coords.latitude,
                longitude: fallbackPos.coords.longitude,
                accuracy: fallbackPos.coords.accuracy
              });
            },
            (fallbackErr) => {
              if (fallbackErr.code === fallbackErr.TIMEOUT) {
                reject(new Error('Location timed out. Please ensure your device GPS/Wi-Fi is on and refresh the page.'));
              } else {
                reject(new Error('Unable to retrieve location: ' + (fallbackErr.message || 'Please check GPS/Wi-Fi settings.')));
              }
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
          );
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      );
    });
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Error accessing camera", err);
      setError("Camera access is required for clocking in.");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setStream(null);
  };

  const isWfhActive = attendanceType === 'WFH' || user?.workLocation === 'WFH';

  const openCameraModal = (action = 'CLOCK_IN') => {
    if (action === 'CLOCK_OUT' && !workReport.trim()) {
      setError('Please provide a work report before clocking out.');
      return;
    }
    setError('');
    setCameraAction(action);
    setShowCameraModal(true);
    startCamera();
  };

  const handleClockIn = async () => {
    // If clocking in for a Company Meeting
    if (attendanceType === 'MEETING') {
      if (!meetingReason.trim()) {
        setError('Please provide meeting details (client, purpose & location) before clocking in.');
        return;
      }
      setError('');
      setActionLoading(true);
      try {
        let location = null;
        try { location = await getLocation(); } catch (e) {}
        await api.post('/attendance/clock-in', {
          location,
          attendanceType: 'MEETING',
          meetingReason: meetingReason.trim(),
          photoBase64: null
        });
        await fetchHistory();
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Clock In failed');
      } finally {
        setActionLoading(false);
      }
      return;
    }

    // If working from office, open camera modal for photo capture
    if (!isWfhActive) {
      openCameraModal('CLOCK_IN');
      return;
    }

    // Direct clock-in for WFH (no photo capture)
    setError('');
    setActionLoading(true);
    try {
      let location = null;
      try { location = await getLocation(); } catch (e) {}
      await api.post('/attendance/clock-in', { location, attendanceType: 'WFH', photoBase64: null });
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Clock In failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!workReport.trim()) {
      setError('Please provide a work report before clocking out.');
      return;
    }

    const isWfhShift = todayRecord?.attendanceType === 'WFH' || user?.workLocation === 'WFH';
    const isMeetingShift = todayRecord?.attendanceType === 'MEETING';

    // If working from office (and not a Meeting or WFH), open camera modal for photo capture
    if (!isWfhShift && !isMeetingShift) {
      openCameraModal('CLOCK_OUT');
      return;
    }

    // Direct clock-out for WFH & Meeting (no photo capture)
    setError('');
    setActionLoading(true);
    try {
      await api.post('/attendance/clock-out', { workReport, photoBase64: null });
      setWorkReport('');
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Clock Out failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCameraCapture = async () => {
    if (!videoRef.current) return;
    setCameraLoading(true);
    
    // Constrain resolution to max 640px width/height for fast uploads and small payload size
    const rawWidth = videoRef.current.videoWidth || 640;
    const rawHeight = videoRef.current.videoHeight || 480;
    const maxDim = 640;
    let targetWidth = rawWidth;
    let targetHeight = rawHeight;

    if (rawWidth > maxDim || rawHeight > maxDim) {
      if (rawWidth > rawHeight) {
        targetWidth = maxDim;
        targetHeight = Math.round((rawHeight * maxDim) / rawWidth);
      } else {
        targetHeight = maxDim;
        targetWidth = Math.round((rawWidth * maxDim) / rawHeight);
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, targetWidth, targetHeight);
    const photoBase64 = canvas.toDataURL('image/jpeg', 0.7);

    try {
      if (cameraAction === 'CLOCK_IN') {
        let location = null;
        if (attendanceType === 'OFFICE') {
          location = await getLocation();
        } else {
          try { location = await getLocation(); } catch (e) {}
        }
        await api.post('/attendance/clock-in', { location, attendanceType, photoBase64, meetingReason: meetingReason.trim() });
      } else {
        const isOfficeShift = todayRecord?.attendanceType === 'OFFICE' && user?.workLocation !== 'WFH';
        let location = null;
        if (isOfficeShift) {
          location = await getLocation();
        } else {
          try { location = await getLocation(); } catch (e) {}
        }
        await api.post('/attendance/clock-out', { workReport, photoBase64, location });
      }

      await fetchHistory();
      setShowCameraModal(false);
      stopCamera();
      
      if (cameraAction === 'CLOCK_OUT') {
        setWorkReport('');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || `${cameraAction === 'CLOCK_IN' ? 'Clock In' : 'Clock Out'} failed`);
    } finally {
      setCameraLoading(false);
    }
  };

  const unresolvedMissedRecord = history.find(r => r.status === 'MISSING_CLOCK_OUT');

  const handleResolveMissedClockOut = async () => {
    if (!graceReason.trim()) {
      setError('Please provide a reason to resolve your missed clock-out.');
      return;
    }
    setError('');
    setGraceLoading(true);
    try {
      await api.post('/attendance/resolve-missed', {
        attendanceId: unresolvedMissedRecord._id,
        graceReason
      });
      setGraceReason('');
      await fetchHistory();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to resolve missed clock out');
    } finally {
      setGraceLoading(false);
    }
  };

  const todayStr = new Date().toLocaleDateString();
  const todayRecord = history.find(r => new Date(r.date).toLocaleDateString() === todayStr);
  const isClockedIn = todayRecord && todayRecord.clockIn && !todayRecord.clockOut;
  const isClockedOut = todayRecord && todayRecord.clockOut;

  const [timeLeft, setTimeLeft] = useState('');
  const [workedTime, setWorkedTime] = useState('');
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    let interval;
    if (isClockedIn && todayRecord) {
      const minHours = user?.customWorkingHours || globalSettings?.fullDayMinHours || 9;
      const clockInTime = new Date(todayRecord.clockIn).getTime();
      const targetTime = clockInTime + minHours * 60 * 60 * 1000;

      const updateTimer = () => {
        const now = new Date().getTime();
        setCurrentTime(now);

        // Worked time so far
        const elapsed = Math.max(0, now - clockInTime);
        const wH = Math.floor(elapsed / (1000 * 60 * 60));
        const wM = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60)).toString().padStart(2, '0');
        const wS = Math.floor((elapsed % (1000 * 60)) / 1000).toString().padStart(2, '0');
        setWorkedTime(`${wH}h ${wM}m ${wS}s`);

        const diff = targetTime - now;
        if (diff <= 0) {
          setTimeLeft(`${minHours} Hours Completed! ✨`);
        } else {
          const h = Math.floor(diff / (1000 * 60 * 60));
          const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)).toString().padStart(2, '0');
          const s = Math.floor((diff % (1000 * 60)) / 1000).toString().padStart(2, '0');
          setTimeLeft(`${h}h ${m}m ${s}s`);
        }
      };
      
      updateTimer();
      interval = setInterval(updateTimer, 1000);
    } else {
      const interval30 = setInterval(() => setCurrentTime(Date.now()), 30000);
      return () => clearInterval(interval30);
    }
    return () => clearInterval(interval);
  }, [isClockedIn, todayRecord, user, globalSettings]);

  const formatWorkingHours = (record) => {
    if (!record) return <span className="text-gray-400">-</span>;

    // 1. Stored workingHours from clock out
    if (record.workingHours && record.workingHours > 0) {
      const totalMinutes = Math.round(record.workingHours * 60);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      const formatted = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
      return (
        <span className="font-semibold text-gray-900" title={`${record.workingHours.toFixed(2)} hrs`}>
          {formatted}
        </span>
      );
    }

    // 2. Both clockIn and clockOut exist
    if (record.clockIn && record.clockOut) {
      const diffMs = new Date(record.clockOut).getTime() - new Date(record.clockIn).getTime();
      if (diffMs > 0) {
        const totalMinutes = Math.round(diffMs / 60000);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        const formatted = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
        return (
          <span className="font-semibold text-gray-900" title={`${(diffMs / 3600000).toFixed(2)} hrs`}>
            {formatted}
          </span>
        );
      }
    }

    // 3. Shift in progress
    if (record.clockIn && !record.clockOut && record.status === 'CLOCKED_IN') {
      const elapsedMs = Math.max(0, currentTime - new Date(record.clockIn).getTime());
      const totalMinutes = Math.floor(elapsedMs / 60000);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      const formatted = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
      return (
        <span className="inline-flex items-center gap-1.5 text-blue-600 font-semibold" title="Shift in progress">
          <span>{formatted}</span>
          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse inline-block" />
        </span>
      );
    }

    return <span className="text-gray-400">-</span>;
  };

  const currentMonthStart = new Date();
  currentMonthStart.setDate(1);
  currentMonthStart.setHours(0, 0, 0, 0);

  const currentMonthHistory = history.filter(r => {
    const d = new Date(r.date || r.clockIn);
    return d >= currentMonthStart;
  });

  const totalEarnedOvertime = overtimeSummary?.totalEarnedMinutes ?? currentMonthHistory.reduce((sum, r) => sum + (r.overtimeMinutes || 0), 0);
  const totalDeductedOvertime = overtimeSummary?.totalDeductedMinutes ?? currentMonthHistory.reduce((sum, r) => sum + (r.overtimeDeductedMinutes || 0), 0);
  const availableOvertimeBalance = overtimeSummary?.availableBalance ?? Math.max(0, totalEarnedOvertime - totalDeductedOvertime);

  const formatMinutesToHours = (mins) => {
    if (!mins || mins <= 0) return '0h 00m';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };

  // Determine if employee is on WFH today (Full-time WFH, Hybrid on WFH day, or Office on approved WFH)
  const isWfhToday = user?.workLocation === 'WFH' 
    || (user?.workLocation === 'HYBRID' && todayHybridSchedule?.type === 'WFH') 
    || hasApprovedWfhToday 
    || attendanceType === 'WFH'
    || todayRecord?.attendanceType === 'WFH';

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto w-full min-w-0">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">Attendance</h1>

      {/* Overtime Summary Cards: Only shown when working from Office (WFH does not earn or track overtime) */}
      {!isWfhToday && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {/* Total Overtime */}
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-2xl p-5 text-white shadow-sm flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-indigo-200">Total Overtime Done</span>
              <p className="text-2xl sm:text-3xl font-bold font-mono mt-1">
                {formatMinutesToHours(totalEarnedOvertime)}
              </p>
              <p className="text-xs text-indigo-200 mt-1">Overtime hours earned this month</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>

          {/* Available Overtime Balance */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-700 rounded-2xl p-5 text-white shadow-sm flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-emerald-200">Overtime Balance</span>
              <p className="text-2xl sm:text-3xl font-bold font-mono mt-1">
                {formatMinutesToHours(availableOvertimeBalance)}
              </p>
              <p className="text-xs text-emerald-200 mt-1">
                {totalDeductedOvertime > 0
                  ? `${formatMinutesToHours(totalDeductedOvertime)} used for early departures`
                  : 'Available to cover early clock-outs'}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8 text-center">
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4">Today's Status</h2>
        
        {unresolvedMissedRecord ? (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-6 animate-fade-in-up">
            <h3 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">Missed Clock-Out Detected</h3>
            <p className="text-gray-800 dark:text-gray-200 mb-4">
              You did not clock out on <span className="font-semibold text-gray-900 dark:text-white">{new Date(unresolvedMissedRecord.date).toLocaleDateString()}</span>. 
              You must provide a reason to resolve this before you can clock in today.
              <br/>
              <span className="text-sm font-semibold text-red-600 dark:text-red-400 mt-2 block">Note: You can only use a grace pass twice per month.</span>
            </p>
            <div className="flex flex-col items-center max-w-md mx-auto mt-4">
              <textarea
                value={graceReason}
                onChange={(e) => setGraceReason(e.target.value)}
                placeholder="Why did you forget to clock out?"
                className="w-full p-3 border rounded-lg mb-4 focus:ring-red-500 focus:border-red-500 shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder-gray-400"
                rows="3"
              />
              <button 
                onClick={handleResolveMissedClockOut} 
                disabled={graceLoading || !graceReason.trim()} 
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg shadow disabled:opacity-50 transition w-full"
              >
                {graceLoading ? 'Processing...' : 'Submit Reason & Resolve'}
              </button>
            </div>
          </div>
        ) : isClockedOut ? (
          <div className="flex flex-col items-center justify-center p-2 animate-fade-in-up">
            {todayRecord?.status === 'PENDING_APPROVAL' ? (
              <div className="text-center mb-3">
                <div className="text-amber-700 font-semibold text-lg mb-1 flex items-center justify-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></span>
                  Meeting Shift Completed • Awaiting Approval
                </div>
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-4 py-2 rounded-xl max-w-md mx-auto">
                  Your meeting attendance request has been submitted to your manager and admin. Once approved, attendance will be credited per your recorded timings.
                </p>
              </div>
            ) : (
              <div className="text-emerald-700 font-semibold text-lg mb-3 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                You have clocked out for today.
              </div>
            )}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 px-8 py-3 rounded-2xl shadow-sm text-center min-w-[240px]">
              <p className="text-xs font-bold text-emerald-800 tracking-widest uppercase mb-1 opacity-80">Total Working Hours</p>
              <p className="text-2xl font-bold text-emerald-700 font-mono tracking-wide">
                {(() => {
                  const hours = todayRecord?.workingHours || (todayRecord?.clockIn && todayRecord?.clockOut ? (new Date(todayRecord.clockOut) - new Date(todayRecord.clockIn)) / (1000 * 60 * 60) : 0);
                  const totalMinutes = Math.round(hours * 60);
                  const h = Math.floor(totalMinutes / 60);
                  const m = totalMinutes % 60;
                  return h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
                })()}
              </p>
              {todayRecord?.overtimeDeductedMinutes > 0 ? (
                <p className="text-xs text-indigo-700 font-semibold mt-1.5 flex items-center justify-center gap-1">
                  <span>⚡</span> {todayRecord.overtimeDeductedMinutes}m Overtime used to protect full day pay
                </p>
              ) : todayRecord?.overtimeMinutes > 0 ? (
                <p className="text-xs text-emerald-700 font-semibold mt-1.5">
                  🎉 +{Math.floor(todayRecord.overtimeMinutes / 60)}h {todayRecord.overtimeMinutes % 60}m Overtime Earned!
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-4">
            {/* Hybrid Schedule Banner */}
            {user?.workLocation === 'HYBRID' && todayHybridSchedule && (
              <div className="w-full max-w-md p-4 rounded-2xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 flex items-center justify-between text-left shadow-sm">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold shadow-sm ${
                    todayHybridSchedule.type === 'WFH' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'
                  }`}>
                    {todayHybridSchedule.type === 'WFH' ? <Home className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-purple-700">Today's Hybrid Schedule</span>
                      {todayHybridSchedule.isOverride && (
                        <span className="text-[10px] font-bold bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">Date Override</span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-gray-800">
                      Assigned Location: <span className={todayHybridSchedule.type === 'WFH' ? 'text-purple-700' : 'text-blue-700'}>
                        {todayHybridSchedule.type === 'WFH' ? '🏠 Work From Home' : '🏢 Office'}
                      </span>
                    </p>
                    {todayHybridSchedule.reason && (
                      <p className="text-xs text-gray-500 italic mt-0.5">"{todayHybridSchedule.reason}"</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {todayHybridSchedule.type === 'WFH' ? (
                    <span className="text-[11px] font-semibold text-purple-700 bg-purple-100/70 px-2.5 py-1 rounded-lg">
                      No photo needed
                    </span>
                  ) : hasApprovedWfhToday ? (
                    <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100/80 px-2.5 py-1 rounded-lg">
                      WFH pass active
                    </span>
                  ) : (
                    <span className="text-[11px] font-semibold text-blue-700 bg-blue-100/70 px-2.5 py-1 rounded-lg">
                      Photo required
                    </span>
                  )}
                </div>
              </div>
            )}

            {!isClockedIn && (
              <div className="flex flex-col items-center space-y-3 mb-2 w-full max-w-lg">
                <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 bg-gray-50 p-3 rounded-2xl border border-gray-100">
                  {/* Office option */}
                  {user?.workLocation !== 'WFH' && (
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="OFFICE"
                        checked={attendanceType === 'OFFICE'}
                        onChange={() => setAttendanceType('OFFICE')}
                        className="form-radio text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-blue-600" /> Office
                      </span>
                    </label>
                  )}

                  {/* WFH option */}
                  {user?.workLocation === 'WFH' ? (
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="WFH"
                        checked={attendanceType === 'WFH'}
                        onChange={() => setAttendanceType('WFH')}
                        className="form-radio text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <Home className="w-4 h-4 text-purple-600" /> Work From Home
                      </span>
                    </label>
                  ) : user?.workLocation === 'HYBRID' ? (
                    <label
                      className={`inline-flex items-center ${
                        todayHybridSchedule?.type === 'WFH' || hasApprovedWfhToday
                          ? 'cursor-pointer'
                          : 'cursor-not-allowed opacity-50'
                      }`}
                      title={
                        todayHybridSchedule?.type === 'OFFICE' && !hasApprovedWfhToday
                          ? 'Scheduled for Office. Approved WFH pass required to work from home.'
                          : ''
                      }
                    >
                      <input
                        type="radio"
                        value="WFH"
                        disabled={todayHybridSchedule?.type === 'OFFICE' && !hasApprovedWfhToday}
                        checked={attendanceType === 'WFH'}
                        onChange={() => {
                          if (todayHybridSchedule?.type === 'WFH' || hasApprovedWfhToday) {
                            setAttendanceType('WFH');
                          }
                        }}
                        className="form-radio text-purple-600 focus:ring-purple-500 disabled:opacity-50"
                      />
                      <span className="ml-2 text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <Home className="w-4 h-4 text-purple-600" /> Work From Home
                      </span>
                    </label>
                  ) : hasApprovedWfhToday ? (
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="WFH"
                        checked={attendanceType === 'WFH'}
                        onChange={() => setAttendanceType('WFH')}
                        className="form-radio text-purple-600"
                      />
                      <span className="ml-2 text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <Home className="w-4 h-4 text-purple-600" /> Work From Home (Approved)
                      </span>
                    </label>
                  ) : null}

                  {/* Company Meeting option */}
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="MEETING"
                      checked={attendanceType === 'MEETING'}
                      onChange={() => setAttendanceType('MEETING')}
                      className="form-radio text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm font-semibold text-indigo-900 flex items-center gap-1.5">
                      <Briefcase className="w-4 h-4 text-indigo-600" /> Company's Meeting
                    </span>
                  </label>
                </div>

                {user?.workLocation === 'HYBRID' && todayHybridSchedule?.type === 'OFFICE' && !hasApprovedWfhToday && attendanceType !== 'MEETING' && (
                  <p className="text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-xl border border-amber-200 text-center max-w-md">
                    ⚠️ Scheduled for <strong>Office</strong> today. An approved WFH request is required to clock in from home.
                  </p>
                )}

                {/* Company Meeting Details Card */}
                {attendanceType === 'MEETING' && (
                  <div className="w-full bg-indigo-50/70 border border-indigo-200 rounded-2xl p-4 text-left animate-fade-in-up">
                    <div className="flex items-center gap-2 mb-1.5 text-indigo-900 font-semibold text-sm">
                      <Briefcase className="w-4 h-4 text-indigo-600" />
                      <span>Company / Client Meeting Details</span>
                    </div>
                    <p className="text-xs text-indigo-700 mb-3 leading-relaxed">
                      You can clock in from anywhere without pre-approval. An approval request will be sent to your manager and admin to verify your attendance timings.
                    </p>
                    <textarea
                      value={meetingReason}
                      onChange={(e) => setMeetingReason(e.target.value)}
                      placeholder="Enter client name, purpose, and meeting location (required)..."
                      className="w-full p-3 text-sm bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm outline-none"
                      rows="2"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-4 w-full justify-center">
              {!isClockedIn ? (
                <>
                  <button onClick={handleClockIn} disabled={actionLoading} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow disabled:opacity-50 transition">
                    {actionLoading ? 'Processing...' : attendanceType === 'MEETING' ? 'Clock In for Meeting' : 'Clock In'}
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center w-full max-w-md">
                  <textarea
                    value={workReport}
                    onChange={(e) => setWorkReport(e.target.value)}
                    placeholder="Briefly describe what you worked on today..."
                    className="w-full p-3 border rounded-lg mb-4 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                    rows="3"
                  />
                  <button onClick={handleClockOut} disabled={actionLoading || !workReport.trim()} className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg shadow disabled:opacity-50 transition w-full">
                    {actionLoading ? 'Processing...' : 'Submit Report & Clock Out'}
                  </button>
                </div>
              )}
            </div>
            
            {isClockedIn && (
              <div className="mt-6 flex flex-col items-center animate-fade-in-up w-full">
                <div className="flex flex-col items-center gap-1.5 mb-3">
                  <p className="text-sm text-gray-500">
                    Clocked in at {new Date(todayRecord.clockIn).toLocaleTimeString()} ({todayRecord.attendanceType})
                  </p>
                  {todayRecord.attendanceType === 'MEETING' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold rounded-full">
                      <Briefcase className="w-3.5 h-3.5 text-indigo-600" /> Company Meeting (Pending Manager/Admin Approval)
                    </span>
                  )}
                  {todayRecord.meetingReason && (
                    <p className="text-xs text-gray-600 italic max-w-md text-center">
                      "{todayRecord.meetingReason}"
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg w-full">
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 px-6 py-3 rounded-2xl shadow-sm text-center">
                    <p className="text-xs font-bold text-emerald-800 tracking-widest uppercase mb-1 opacity-80">Working Hours Today</p>
                    <p className="text-2xl font-bold text-emerald-600 font-mono tracking-wide">{workedTime || '0h 00m 00s'}</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 px-6 py-3 rounded-2xl shadow-sm text-center">
                    <p className="text-xs font-bold text-blue-800 tracking-widest uppercase mb-1 opacity-80">Full Day Countdown</p>
                    <p className="text-2xl font-bold text-blue-600 font-mono tracking-wide">{timeLeft}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>


      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100">
        <div className="px-4 sm:px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-800 text-sm sm:text-base">History</h3>
            <Link
              to="/employee/holidays"
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 transition-colors"
            >
              <Calendar className="w-3.5 h-3.5" />
              Company Holidays
            </Link>
          </div>
          <span className="text-xs text-gray-400 block sm:hidden">Swipe sideways &rarr;</span>
        </div>
        <div className="overflow-x-auto w-full custom-scrollbar">
          <table className="w-full min-w-[700px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clock In</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clock Out</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Working Hours</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Overtime</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work Report / Notes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="9" className="px-6 py-4 text-center">Loading...</td></tr>
              ) : history.length === 0 ? (
                <tr><td colSpan="9" className="px-6 py-4 text-center">No records found</td></tr>
              ) : (
                history.map(record => (
                  <tr key={record._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(record.date).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.attendanceType === 'MEETING' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                          <Briefcase className="w-3 h-3 text-indigo-600" /> MEETING
                        </span>
                      ) : (
                        record.attendanceType
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${record.status === 'PRESENT' ? 'bg-green-100 text-green-800' : 
                          record.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-800' : 
                          record.status === 'CLOCKED_IN' ? 'bg-blue-100 text-blue-800' :
                          record.status === 'HOLIDAY' ? 'bg-purple-100 text-purple-800' :
                          record.status === 'PENDING_APPROVAL' ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'}`}>
                        {record.status === 'PENDING_APPROVAL' ? 'PENDING APPROVAL' : record.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.clockIn ? new Date(record.clockIn).toLocaleTimeString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.clockOut ? new Date(record.clockOut).toLocaleTimeString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatWorkingHours(record)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {record.overtimeMinutes > 0 ? (
                        <span className="text-emerald-600 font-semibold font-mono">
                          +{Math.floor(record.overtimeMinutes / 60)}h {record.overtimeMinutes % 60}m
                        </span>
                      ) : null}
                      {record.overtimeDeductedMinutes > 0 ? (
                        <span className="text-amber-600 font-medium font-mono text-xs block">
                          -{record.overtimeDeductedMinutes}m used
                        </span>
                      ) : null}
                      {!record.overtimeMinutes && !record.overtimeDeductedMinutes && (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col gap-1 items-start">
                        {record.workReport && (
                          <button 
                            onClick={() => setSelectedReport({ title: 'Work Report', content: record.workReport })}
                            className="text-purple-600 hover:text-purple-900 underline text-xs font-medium"
                          >
                            Work Report
                          </button>
                        )}
                        {record.meetingReason && (
                          <button 
                            onClick={() => setSelectedReport({ title: 'Company Meeting Details', content: record.meetingReason })}
                            className="text-indigo-600 hover:text-indigo-900 underline text-xs font-medium flex items-center gap-1"
                          >
                            <Briefcase className="w-3 h-3" /> Meeting Note
                          </button>
                        )}
                        {!record.workReport && !record.meetingReason && (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        {record.livePhotoUrl ? (
                          <a href={record.livePhotoUrl} title="Clock In Photo" target="_blank" rel="noopener noreferrer">
                            <img 
                              src={record.livePhotoUrl} 
                              alt="Clock-in" 
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              className="w-10 h-10 object-cover rounded shadow-sm border border-gray-200 hover:opacity-80 transition-opacity"
                            />
                          </a>
                        ) : null}
                        {record.clockOutPhotoUrl ? (
                          <a href={record.clockOutPhotoUrl} title="Clock Out Photo" target="_blank" rel="noopener noreferrer">
                            <img 
                              src={record.clockOutPhotoUrl} 
                              alt="Clock-out" 
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              className="w-10 h-10 object-cover rounded shadow-sm border border-gray-200 hover:opacity-80 transition-opacity"
                            />
                          </a>
                        ) : null}
                        {!record.livePhotoUrl && !record.clockOutPhotoUrl && (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-4 text-gray-900">
              {typeof selectedReport === 'object' ? selectedReport.title : 'Work Report'}
            </h3>
            <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
              {typeof selectedReport === 'object' ? selectedReport.content : selectedReport}
            </p>
            <div className="mt-6 text-right">
              <button 
                onClick={() => setSelectedReport(null)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Live Camera Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-fade-in-up">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-xl font-bold text-gray-800">
                {cameraAction === 'CLOCK_IN' ? 'Capture Live Photo' : 'Capture Clock Out Photo'}
              </h3>
              <button 
                onClick={() => { setShowCameraModal(false); stopCamera(); }}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 flex flex-col items-center">
              <p className="text-sm text-gray-600 mb-4 text-center">
                Please look at the camera to verify your identity for {cameraAction === 'CLOCK_IN' ? 'Clock In' : 'Clock Out'}.
              </p>
              
              <div className="relative w-full max-w-sm aspect-video bg-gray-900 rounded-xl overflow-hidden shadow-inner mb-6">
                {!stream && !error && (
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                    Initializing camera...
                  </div>
                )}
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted 
                  className={`w-full h-full object-cover ${stream ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
                />
              </div>

              {error && (
                <div className="w-full p-3 mb-6 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  onClick={() => { setShowCameraModal(false); stopCamera(); }}
                  className="flex-1 sm:flex-none px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCameraCapture}
                  disabled={!stream || cameraLoading}
                  className="flex-1 sm:flex-none px-8 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium shadow-sm shadow-blue-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {cameraLoading ? 'Processing...' : (cameraAction === 'CLOCK_IN' ? 'Capture & Clock In' : 'Capture & Clock Out')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
